import React, { useEffect, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ThemeProvider } from '@mui/material/styles';
import ChatInterface from '../ChatInterface';
import { LabBasketProvider } from '../../contexts/LabBasketContext';
import LabBasketStrip from './LabBasketStrip';
import { useWebSocket } from '../../contexts/WebSocketContext';
import theme from '../../theme';
import { LABOKLIN_APPBAR_HEIGHT, LABOKLIN_BORDER, LABOKLIN_RED } from '../../theme/laboklinTheme';

/**
 * GekkoVet assistant, as a drawer over the MyLab content.
 *
 * Hand-rolled rather than MUI's Drawer for one reason: ChatInterface pins its
 * composer with `position: fixed`, which resolves against the viewport unless an
 * ancestor establishes a containing block. The content wrapper below does that
 * with a static `translateZ(0)` - a transform that never animates, so the
 * containing block cannot disappear mid-transition the way it can under a
 * Drawer's sliding paper.
 *
 * ChatInterface is otherwise reused unchanged, under the app's own GekkoVet
 * theme. The chrome around it stays Laboklin's; the assistant stays visibly
 * GekkoVet, which is the point of the integration rather than an oversight.
 */

const PANEL_HEADER_HEIGHT = 45;

interface CopilotPanelProps {
  initialMessage: string;
  labMode: 'pre_order' | 'post_result';
  labReportNo?: string;
  onClose: () => void;
}

export const CopilotPanel: React.FC<CopilotPanelProps> = ({
  initialMessage,
  labMode,
  labReportNo,
  onClose,
}) => {
  const { connectToConversation } = useWebSocket();

  // Generated once: the socket manager tears down and reconnects whenever the
  // conversation id changes, so a per-render id would loop.
  const [conversationId] = useState(() => crypto.randomUUID());

  useEffect(() => {
    connectToConversation(conversationId, {
      partner: 'laboklin',
      labReportNo,
      labMode,
    }).catch((err) => {
      console.error('Failed to connect Laboklin copilot conversation:', err);
    });
  }, [conversationId, labReportNo, labMode, connectToConversation]);

  // Escape closes, matching what a drawer is expected to do.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <>
      <Box
        onClick={onClose}
        sx={{
          position: 'fixed',
          inset: 0,
          top: LABOKLIN_APPBAR_HEIGHT,
          bgcolor: 'rgba(0, 0, 0, 0.35)',
          zIndex: 1200,
        }}
      />

      <Box
        role="dialog"
        aria-label="Diagnostic assistant"
        sx={{
          position: 'fixed',
          top: LABOKLIN_APPBAR_HEIGHT,
          right: 0,
          bottom: 0,
          width: { xs: '100%', md: '90%' },
          maxWidth: 1100,
          zIndex: 1201,
          bgcolor: 'background.paper',
          borderLeft: `1px solid ${LABOKLIN_BORDER}`,
          boxShadow: '-8px 0 24px rgba(0,0,0,0.18)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            height: PANEL_HEADER_HEIGHT,
            flexShrink: 0,
            borderBottom: `1px solid ${LABOKLIN_BORDER}`,
            bgcolor: 'background.paper',
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: 15 }}>
            Diagnostic assistant
            <Typography component="span" sx={{ color: 'text.secondary', ml: 1, fontSize: 13 }}>
              powered by GekkoVet
            </Typography>
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close assistant">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* The transform is load-bearing: it makes this box the containing block
            for ChatInterface's fixed composer, keeping it inside the drawer. */}
        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflow: 'hidden',
            position: 'relative',
            transform: 'translateZ(0)',
            bgcolor: 'background.paper',
          }}
        >
          <ThemeProvider theme={theme}>
            <LabBasketProvider>
            <LabBasketStrip />
            <ChatInterface
              initialConversationId={conversationId}
              initialMessage={initialMessage}
              topOffsetPx={LABOKLIN_APPBAR_HEIGHT + PANEL_HEADER_HEIGHT}
              embedded
              emptyState={
                <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography sx={{ fontWeight: 600, color: LABOKLIN_RED, mb: 1 }}>
                    Reading the laboratory data…
                  </Typography>
                  <Typography variant="body2">
                    {labReportNo
                      ? `Preparing an interpretation of report ${labReportNo}.`
                      : 'Preparing test suggestions for this patient.'}
                  </Typography>
                </Box>
              }
            />
            </LabBasketProvider>
          </ThemeProvider>
        </Box>
      </Box>
    </>
  );
};

export default CopilotPanel;
